import React, { useEffect, useState } from 'react'
import { portal } from '../portalApi.js'
import { fmt } from '../api.js'
import NoteDrawer from '../components/NoteDrawer.jsx'
import Subitems from './Subitems.jsx'
import Cards from './Cards.jsx'
import Headcount from './Headcount.jsx'
import LabelRules from './LabelRules.jsx'
import AccountDepts from './AccountDepts.jsx'

const KINDS = [
  { key: 'general', label: '일반' },
  { key: 'bank', label: '금융' },
  { key: 'card', label: '카드' },
]
const LIMIT = 50
const EMPTY_FORM = { code: '', name: '', bizNo: '', repName: '', accountNo: '', category: '', used: '1',
  fundItemIn: '', fundItemOut: '' }

// 자금항목 선택칸. 한 거래처가 팔기도 하고 사기도 하므로 방향별로 따로 고른다 —
// 쿠팡은 들어오면 온라인(수입), 나가면 경비(지출)다.
function FundPicker({ funds, side, value, onChange }) {
  return (
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}
      style={{ maxWidth: 180 }}>
      <option value="">지정 안 함</option>
      {funds.filter((f) => f['구분'] === side).map((f) => (
        <option key={f.id} value={f.id}>{f['항목명']}</option>
      ))}
    </select>
  )
}

function VendorFormModal({ kind, initial, funds, onSaved, onClose }) {
  const [form, setForm] = useState(initial || { ...EMPTY_FORM })
  const [err, setErr] = useState('')
  const isEdit = !!initial

  const save = async () => {
    if (!form.name.trim()) return
    setErr('')
    try {
      await portal.post('/api/vendors', { kind, ...form })
      onSaved()
    } catch (e) { setErr(e.message) }
  }

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div className="modal-panel" style={{ width: 420 }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEdit ? '거래처 수정' : `거래처 추가 · ${KINDS.find((k) => k.key === kind)?.label}`}</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>
        <div style={{ padding: '4px 16px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {err && <div className="error-box">{err}</div>}
          <label className="muted" style={{ fontSize: 12 }}>거래처명*
            <input style={{ width: '100%' }} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </label>
          {kind === 'general' && (
            <>
              <label className="muted" style={{ fontSize: 12 }}>사업자번호
                <input style={{ width: '100%' }} value={form.bizNo} onChange={(e) => setForm({ ...form, bizNo: e.target.value })} />
              </label>
              <label className="muted" style={{ fontSize: 12 }}>대표자
                <input style={{ width: '100%' }} value={form.repName} onChange={(e) => setForm({ ...form, repName: e.target.value })} />
              </label>
            </>
          )}
          {kind === 'general' && funds.length > 0 && (
            <>
              <div className="hint" style={{ margin: '10px 0 8px' }}>
                이 거래처의 거래가 자금계획 어느 항목으로 갈지 미리 정해 둡니다. 정해 두면
                그 거래처의 과거·앞으로의 거래에 자동으로 붙습니다. 방향별로 다를 수 있어
                수입과 지출을 따로 고릅니다.
              </div>
              <div className="row" style={{ marginBottom: 10 }}>
                <span className="muted" style={{ width: 110 }}>자금항목 · 수입</span>
                <FundPicker funds={funds} side="수입" value={form.fundItemIn}
                  onChange={(v) => setForm({ ...form, fundItemIn: v })} />
              </div>
              <div className="row" style={{ marginBottom: 10 }}>
                <span className="muted" style={{ width: 110 }}>자금항목 · 지출</span>
                <FundPicker funds={funds} side="지출" value={form.fundItemOut}
                  onChange={(v) => setForm({ ...form, fundItemOut: v })} />
              </div>
            </>
          )}
          {(kind === 'bank' || kind === 'card') && (
            <label className="muted" style={{ fontSize: 12 }}>계좌번호
              <input style={{ width: '100%' }} value={form.accountNo} onChange={(e) => setForm({ ...form, accountNo: e.target.value })} />
            </label>
          )}
          <label className="muted" style={{ fontSize: 12 }}>분류
            <input style={{ width: '100%' }} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          </label>
          <label className="row" style={{ gap: 6, fontSize: 13 }}>
            <input type="checkbox" checked={form.used === '1'} onChange={(e) => setForm({ ...form, used: e.target.checked ? '1' : '0' })} />
            사용
          </label>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn secondary" onClick={onClose}>취소</button>
          <button type="button" className="btn" disabled={!form.name.trim()} onClick={save}>저장</button>
        </div>
      </div>
    </div>
  )
}

function VendorsPane({ currentUserId }) {
  const [kind, setKind] = useState('general')
  const [funds, setFunds] = useState([])
  const [q, setQ] = useState('')
  const [query, setQuery] = useState('')
  const [offset, setOffset] = useState(0)
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const [editing, setEditing] = useState(null) // null | {} (신규) | vendor (수정)
  const [toast, setToast] = useState('')
  const [uploading, setUploading] = useState(false)
  const fileInputRef = React.useRef(null)

  const notify = (m) => { setToast(m); setTimeout(() => setToast(''), 2500) }

  const load = () => {
    setErr('')
    portal.get(`/api/vendors?kind=${kind}&q=${encodeURIComponent(query)}&limit=${LIMIT}&offset=${offset}`)
      .then(setData)
      .catch((e) => setErr(e.status === 403 ? '거래처 화면 권한이 없습니다. 관리자에게 권한을 요청하세요.' : e.message))
  }
  useEffect(() => { load() }, [kind, query, offset])
  useEffect(() => { portal.get('/api/fund-plan/items').then(setFunds).catch(() => {}) }, [])

  const search = (e) => { e.preventDefault(); setOffset(0); setQuery(q.trim()) }

  const removeVendor = async (v) => {
    if (!confirm(`"${v.name}" 거래처를 삭제할까요?`)) return
    try { await portal.del(`/api/vendors/${kind}/${encodeURIComponent(v.code)}`); load() }
    catch (e) { setErr(e.message) }
  }

  const uploadExcel = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setUploading(true); setErr('')
    try {
      const fd = new FormData()
      fd.append('kind', kind)
      fd.append('file', file)
      const res = await fetch('/api/vendors/import', { method: 'POST', body: fd, credentials: 'same-origin' })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body.error || res.statusText)
      notify(`위하고 엑셀 반영 완료 (${body.count}건)`)
      load()
    } catch (e) { setErr(e.message) } finally { setUploading(false) }
  }

  return (
    <>
      <div className="row" style={{ marginBottom: 14 }}>
        {KINDS.map((k) => (
          <button key={k.key} className={`btn ${kind === k.key ? '' : 'secondary'}`}
            onClick={() => { setKind(k.key); setOffset(0) }}>
            {k.label}{data?.counts?.[k.key] !== undefined ? ` ${fmt(data.counts[k.key])}` : ''}
          </button>
        ))}
        <form onSubmit={search} className="row" style={{ gap: 6 }}>
          <input placeholder="거래처명·코드·사업자번호·계좌번호 검색" style={{ width: 260 }}
            value={q} onChange={(e) => setQ(e.target.value)} />
          <button className="btn secondary" type="submit">검색</button>
        </form>
        <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={uploadExcel} />
        <button className="btn secondary" style={{ marginLeft: 'auto' }} disabled={uploading}
          onClick={() => fileInputRef.current?.click()}>
          {uploading ? '업로드 중…' : `위하고 엑셀 업로드 (${KINDS.find((k) => k.key === kind)?.label})`}
        </button>
        <button className="btn" onClick={() => setEditing({})}>+ 거래처 추가</button>
      </div>
      <div className="hint" style={{ marginBottom: 10 }}>
        위하고에서 내려받은 "거래처등록_*.xlsx"를 그대로 올리면 코드 기준으로 갱신됩니다(기존 값 덮어씀). 직접 추가한 거래처는 영향 없습니다.
      </div>
      {err && <div className="error-box">{err}</div>}
      {!err && !data && <div className="muted">불러오는 중…</div>}
      {data && (
        <div className="panel">
          <table>
            <thead>
              <tr><th>코드</th><th className="l">거래처명</th><th>사업자번호</th><th className="l">대표자</th><th className="l">계좌번호</th><th className="l">분류</th>
                {kind === 'general' && <th className="l">자금항목 (수입 / 지출)</th>}
                <th>사용</th><th>연동</th><th /></tr>
            </thead>
            <tbody>
              {data.vendors.length === 0 && <tr><td colSpan={9} className="c muted">결과가 없습니다</td></tr>}
              {data.vendors.map((v) => (
                <tr key={v.code}>
                  <td className="c">{v.code}</td>
                  <td className="l">{v.name}</td>
                  <td className="c">{v.bizNo}</td>
                  <td className="l">{v.repName}</td>
                  <td className="l">{v.accountNo}</td>
                  <td className="l">{v.category}</td>
                  {kind === 'general' && (
                    <td className="l">
                      {(() => {
                        const nm = (id) => funds.find((f) => f.id === id)?.['항목명']
                        const i = nm(v.fundItemIn); const o = nm(v.fundItemOut)
                        if (!i && !o) return <span className="muted">—</span>
                        return <span>{i || '—'} <span className="muted">/</span> {o || '—'}</span>
                      })()}
                    </td>
                  )}
                  <td className="c">{v.used ? '○' : ''}</td>
                  <td className="c">{v.linked ? '○' : ''}</td>
                  <td className="c" style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn secondary" style={{ padding: '3px 8px', fontSize: 11 }} onClick={() => setEditing(v)}>수정</button>{' '}
                    <button className="btn danger" style={{ padding: '3px 8px', fontSize: 11 }} onClick={() => removeVendor(v)}>삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="row" style={{ marginTop: 12 }}>
            <button className="btn secondary" disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - LIMIT))}>이전</button>
            <span className="muted">{fmt(offset + 1)}–{fmt(Math.min(offset + LIMIT, data.total))} / {fmt(data.total)}</span>
            <button className="btn secondary" disabled={offset + LIMIT >= data.total}
              onClick={() => setOffset(offset + LIMIT)}>다음</button>
          </div>
        </div>
      )}
      {editing && (
        <VendorFormModal
          kind={kind}
          initial={editing.code ? editing : null}
          funds={funds}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); notify('저장 완료'); load() }}
        />
      )}
      {toast && <div className="toast">{toast}</div>}
    </>
  )
}

function CategoriesPane({ isAdmin }) {
  const [cats, setCats] = useState(null)
  const [err, setErr] = useState('')
  const [toast, setToast] = useState('')
  const [uploading, setUploading] = useState(false)
  const fileInputRef = React.useRef(null)

  const notify = (m) => { setToast(m); setTimeout(() => setToast(''), 2500) }

  const load = () => {
    portal.get('/api/fa/meta/categories').then(setCats).catch((e) => setErr(e.message))
  }
  useEffect(() => { load() }, [])

  const removeCategory = async (code) => {
    try { await portal.del(`/api/fa/meta/categories/curated/${encodeURIComponent(code)}`); load() }
    catch (e) { setErr(e.message) }
  }

  const uploadExcel = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setUploading(true); setErr('')
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await fetch('/api/fa/meta/categories/import', { method: 'POST', body: fd, credentials: 'same-origin' })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body.error || res.statusText)
      notify(`위하고 계정과목 반영 완료 (${body.count}건)`)
      load()
    } catch (e) { setErr(e.message) } finally { setUploading(false) }
  }

  const breadcrumb = (c) => [c.major, c.minor].filter(Boolean).join(' > ')

  return (
    <div className="panel">
      <div className="hint">
        위하고 계정과목표를 그대로 씁니다(계정과목 선택은 우리 DB에만 저장됩니다).
        {isAdmin ? ' 위하고에서 내려받은 "계정과목.xlsx"를 올리면 코드 기준으로 통째 갱신됩니다.' : ''}
      </div>
      {isAdmin && (
        <div className="row" style={{ margin: '10px 0' }}>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={uploadExcel} />
          <button className="btn secondary" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
            {uploading ? '업로드 중…' : '위하고 계정과목 엑셀 업로드'}
          </button>
        </div>
      )}
      {err && <div className="error-box">{err}</div>}
      {!err && !cats && <div className="muted">불러오는 중…</div>}
      {cats && (
        <table style={{ maxWidth: 620 }}>
          <thead><tr><th className="l">분류</th><th>코드</th><th className="l">계정과목</th>{isAdmin && <th />}</tr></thead>
          <tbody>
            {cats.length === 0 && <tr><td colSpan={isAdmin ? 4 : 3} className="c muted">등록된 계정과목이 없습니다</td></tr>}
            {cats.map((c) => (
              <tr key={c.code}>
                <td className="l muted" style={{ fontSize: 12 }}>{breadcrumb(c)}</td>
                <td className="c">{c.code}</td>
                <td className="l">{c.name}</td>
                {isAdmin && <td className="c"><button className="btn danger" onClick={() => removeCategory(c.code)}>삭제</button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {toast && <div className="toast">{toast}</div>}
    </div>
  )
}

export default function Vendors({ currentUserId, autoOpenNoteId, onAutoOpened, isAdmin,
                                  subTab, onSubOpened }) {
  const [tab, setTab] = useState(subTab || 'vendors')
  // 다른 화면에서 «계좌 설정»으로 건너오면 그 탭을 펴고 들어간다
  useEffect(() => {
    if (!subTab) return
    setTab(subTab)
    onSubOpened?.()
  }, [subTab])

  return (
    <>
      <div style={{ position: 'relative' }}>
        <h2>기초설정</h2>
        <NoteDrawer target="d-vendors" screenKey="data_vendors"
          context={null} contextLabel={null} onNavigate={() => {}}
          autoOpenNoteId={autoOpenNoteId} onAutoOpened={onAutoOpened}
          currentUserId={currentUserId} />
      </div>
      {/* 탭은 «지금 어디에 있나»를 알려 주는 것이지 누를 것을 늘어놓는 자리가 아니다.
          알약 단추 일곱 개가 나란히 서면 전부 같은 무게로 보여 고른 것이 묻힌다.
          업무관리 상태 탭과 같은 모양을 쓴다 - 화면마다 다른 탭을 만들 이유가 없다 */}
      <div className="data-tabs">
        {[
          ['vendors', '거래처'],
          ['categories', '계정과목'],
          // 세목은 계정과목 아래 3차다 - 손익은 2차로 나가고 안쪽만 잘게 본다
          ['subitems', '세목'],
          // 계좌에 대한 설정은 전부 여기다 - 별칭·계좌유형·사업부·부서를 한자리에서
          ['accounts', '계좌'],
          ['cards', '법인카드'],
          // 공통비 배부와 인당생산성이 이 인원을 쓴다
          ['headcount', '부서·인원'],
          // 분류를 붙이는 규칙. 사람 > 규칙 > 카드 부서 순으로 이긴다
          ['rules', '분류규칙'],
        ].map(([k, label]) => (
          <button key={k} type="button"
            className={'data-tab' + (tab === k ? ' active' : '')}
            onClick={() => setTab(k)}>{label}</button>
        ))}
      </div>
      {tab === 'vendors' && <VendorsPane currentUserId={currentUserId} />}
      {tab === 'categories' && <CategoriesPane isAdmin={isAdmin} />}
      {tab === 'subitems' && <Subitems />}
      {tab === 'accounts' && <AccountDepts />}
      {tab === 'cards' && <Cards />}
      {tab === 'headcount' && <Headcount />}
      {tab === 'rules' && <LabelRules />}
    </>
  )
}
